import { SxProps } from '@mui/material'
import { createTheme } from '@mui/material/styles'

declare module '@mui/material/styles' {
  interface TypographyVariants {
    sectionHeadline: React.CSSProperties
    sectionTitle: React.CSSProperties
    sectionTitle2: React.CSSProperties
    sectionTitleSmall: React.CSSProperties
    listItemTitle: React.CSSProperties
    listItemTitlePrimary: React.CSSProperties
    listItemDescription: React.CSSProperties
    listItemDescriptionLight: React.CSSProperties
    label1: React.CSSProperties
    label2: React.CSSProperties
    label3: React.CSSProperties
  }

  interface TypographyVariantsOptions {
    sectionHeadline?: React.CSSProperties
    sectionTitle?: React.CSSProperties
    sectionTitle2?: React.CSSProperties
    sectionSmall?: React.CSSProperties
    listItemTitle?: React.CSSProperties
    listItemTitlePrimary?: React.CSSProperties
    listItemDescription?: React.CSSProperties
    listItemDescriptionLight?: React.CSSProperties
    label1?: React.CSSProperties
    label2?: React.CSSProperties
    label3?: React.CSSProperties
  }

  interface Palette {
    gray: {
      1: string
      2: string
      3: string
      4: string
    }
  }

  interface PaletteOptions {
    gray: {
      1: string
      2: string
      3: string
      4: string
    }
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    sectionHeadline: true
    sectionTitle: true
    sectionTitle2: true
    sectionTitleSmall: true
    listItemTitle: true
    listItemTitlePrimary: true
    listItemDescription: true
    listItemDescriptionLight: true
    content1: true
    description1: true
    subtitle3: true
    label1: true
    label2: true
    label3: true
    tab: true
  }

  interface TypographyPropsColorOverrides {
    gray: true
  }
}

export const IconStyle: SxProps = {
  height: { xs: '36px', md: '40px' },
  width: { xs: '36px', md: '40px' }
}

export const SummaryPaperStyle = {
  borderRadius: '26px',
  border: 'none',
  padding: { xs: '30px', md: '40px' }
}

export const SummaryPaperLargeStyle = {
  height: { xs: '100%', md: '400px' }
}

export const SummaryPaperSmallStyle = {
  padding: { xs: '30px', md: '40px' },
  height: { xs: '176px', md: '192px' }
}

export const EllipsisStyle = {
  maxWidth: '100%',
  '& > .MuiTypography-root': { maxWidth: 'inherit', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }
}

export const columnListButtonStyle = {
  width: { xs: '216px', md: '236px' }
}

export const getDefaultTheme = (isDark) => {
  const theme = createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        main: '#0381FE',
        dark: '#0072DE',
        contrastText: '#FAFAFA'
      },
      error: {
        main: '#DB322A'
      },
      action: {
        disabledBackground: '',
        disabled: ''
      },
      gray: {
        1: '#636363',
        2: '#8C8C8C',
        3: '#A9A9A9',
        4: '#E5E5E5'
      },
      ...(isDark
        ? {
            secondary: {
              main: '#303030',
              dark: '#2A2A2A',
              contrastText: '#FFFFFF',
              light: '#636363'
            },
            background: {
              paper: '#171717',
              default: '#010101'
            },
            text: {
              primary: '#E5E5E5',
              secondary: '#A9A9A9'
            },
            info: {
              main: '#636363',
              dark: '#999999'
            },
            buttonSecondary: {
              main: '#636363',
              contrastText: '#FAFAFA'
            }
          }
        : {
            secondary: {
              main: '#E5E5E5',
              dark: '#D0D0D0',
              contrastText: '#252525',
              light: '#A9A9A9'
            },
            background: {
              paper: '#FCFCFC',
              default: '#F2F2F2'
            },
            text: {
              primary: '#252525',
              secondary: '#4D4D4D'
            },
            info: {
              main: '#999999',
              dark: '#636363'
            },
            buttonSecondary: {
              main: '#E5E5E5',
              contrastText: '#252525'
            }
          })
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 640,
        md: 900,
        lg: 1280,
        xl: 1440
      }
    },
    typography: {
      fontFamily: ['SamsungOne', 'SamsungOneKorean'].join(',')
    }
  })
  return createTheme(theme, {
    typography: {
      sectionHeadline: {
        color: theme.palette.gray[1],
        fontSize: '24px',
        lineHeight: '34px',
        fontWeight: 500,
        [theme.breakpoints.only('xs')]: {
          fontSize: '14px',
          lineHeight: '20px'
        }
      },
      sectionTitle: {
        color: theme.palette.text.primary,
        fontSize: '32px',
        lineHeight: '40px',
        fontWeight: 500,
        [theme.breakpoints.only('xs')]: {
          fontSize: '24px',
          lineHeight: '29px'
        }
      },
      sectionTitle2: {
        color: theme.palette.text.primary,
        fontSize: '32px',
        lineHeight: '40px',
        fontWeight: 300,
        [theme.breakpoints.only('xs')]: {
          fontSize: '24px',
          lineHeight: '28px',
          fontWeight: 500
        }
      },
      sectionTitleSmall: {
        color: theme.palette.text.primary,
        fontSize: '27px',
        lineHeight: '33px',
        fontWeight: 600
      },
      listItemTitle: {
        color: theme.palette.mode === 'dark' ? '#E5E5E5' : theme.palette.text.primary,
        fontSize: '18px',
        lineHeight: '24px',
        fontWeight: 500
      },
      listItemTitlePrimary: {
        color: theme.palette.text.primary,
        fontSize: '18px',
        lineHeight: '24px',
        fontWeight: 500
      },
      listItemDescription: {
        color: theme.palette.mode === 'dark' ? '#636363' : theme.palette.gray[3],
        fontSize: '16px',
        lineHeight: '22px',
        fontWeight: 500,
        [theme.breakpoints.down('lg')]: {
          fontSize: '13px',
          lineHeight: '16px'
        }
      },
      listItemDescriptionLight: {
        color: theme.palette.secondary.light,
        fontSize: '16px',
        lineHeight: '22px',
        fontWeight: 500,
        [theme.breakpoints.down('lg')]: {
          fontSize: '13px',
          lineHeight: '16px'
        }
      },
      h1: {
        color: theme.palette.text.primary,
        fontSize: '56px',
        lineHeight: '66px',
        fontWeight: 300,
        [theme.breakpoints.only('xs')]: {
          fontSize: '40px',
          lineHeight: '48px'
        }
      },
      h2: {
        color: theme.palette.text.primary,
        fontSize: '32px',
        lineHeight: '40px',
        fontWeight: 500,
        [theme.breakpoints.only('xs')]: {
          fontSize: '24px',
          lineHeight: '29px'
        }
      },
      h3: {
        color: theme.palette.text.primary,
        fontSize: '18px',
        lineHeight: '21px',
        fontWeight: 500,
        [theme.breakpoints.only('xs')]: {
          fontSize: '15px',
          lineHeight: '18px'
        }
      },
      subtitle1: {
        color: theme.palette.gray[1],
        fontSize: '24px',
        lineHeight: '34px',
        fontWeight: 500,
        [theme.breakpoints.only('xs')]: {
          fontSize: '14px',
          lineHeight: '20px'
        }
      },
      subtitle2: {
        color: theme.palette.text.primary,
        fontSize: '14px',
        lineHeight: '18px',
        fontWeight: 700,
        [theme.breakpoints.only('xs')]: {
          fontSize: '13px',
          lineHeight: '16px'
        }
      },
      subtitle3: {
        color: theme.palette.mode === 'dark' ? '#A9A9A9' : theme.palette.gray[1],
        fontSize: '18px',
        lineHeight: '21px',
        fontWeight: 400,
        [theme.breakpoints.only('xs')]: {
          fontSize: '15px',
          lineHeight: '18px'
        }
      },
      body1: {
        color: theme.palette.text.primary,
        fontSize: '16px',
        lineHeight: '24px',
        fontWeight: 500
      },
      body2: {
        color: theme.palette.text.primary,
        fontSize: '18px',
        lineHeight: '24px',
        fontWeight: 500
      },
      label1: {
        color: theme.palette.text.secondary,
        fontSize: '16px',
        lineHeight: '14px',
        fontWeight: 500
      },
      label2: {
        color: theme.palette.text.secondary,
        fontSize: '12px',
        lineHeight: '14px',
        fontWeight: 500
      },
      label3: {
        color: theme.palette.mode === 'dark' ? '#A9A9A9' : '#505050',
        fontSize: '12px',
        lineHeight: '16px',
        fontWeight: 500
      }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
        ::-ms-reveal {
          display: none;
        }

        :focus-visible:not(.MuiInputBase-input):not(.MuiOutlinedInput-input):not(.MuiFilledInput-input):not(.MuiInput-input):not(.MuiButton-root), 
        .MuiButton-root:has(.MuiButton-endIcon):focus-visible {
          outline: 2px solid ${isDark ? '#98CBFD' : '#0072DE'};
          border-radius: 0px;
        }

        @supports(-webkit-touch-callout: none){
	        :focus-visible:not(.MuiInputBase-input):not(.MuiOutlinedInput-input):not(.MuiFilledInput-input):not(.MuiInput-input):not(.MuiButton-root), 
          .MuiButton-root:has(.MuiButton-endIcon):focus-visible {
		        -webkit-focus-ring-color: 2px solid ${isDark ? '#98CBFD' : '#0072DE'};
            border-radius: 0px;
	        }
        }
          
        .MuiFormControlLabel-root:focus-within:has(input:focus-visible) {
          outline: 2px solid ${isDark ? '#98CBFD' : '#0072DE'};
        }

        .MuiSwitch-root:focus-within:has(input:focus-visible) {
          outline: 2px solid ${isDark ? '#98CBFD' : '#0072DE'};
        }

        /* samsung One */
        @font-face {
          font-family: 'SamsungOne';
          font-style: normal;
          font-weight: 200;
          font-display: swap;
          src: url(/assets/fonts/SamsungOne-200_v2.0.woff) format('woff');
        }
        @font-face {
          font-family: 'SamsungOne';
          font-style: normal;
          font-weight: 300;
          font-display: swap;
          src: url(/assets/fonts/SamsungOne-300_v2.0.woff) format('woff');
        }
        @font-face {
          font-family: 'SamsungOne';
          font-style: normal;
          font-weight: 400;
          font-display: swap;
          src: url(/assets/fonts/SamsungOne-400_v2.0.woff) format('woff');
        }
        @font-face {
          font-family: 'SamsungOne';
          font-style: normal;
          font-weight: 500;
          font-display: swap;
          src: url(/assets/fonts/SamsungOne-500_v2.0.woff) format('woff');
        }
        @font-face {
          font-family: 'SamsungOne';
          font-style: normal;
          font-weight: 600;
          font-display: swap;
          src: url(/assets/fonts/SamsungOne-600_v2.0.woff) format('woff');
        }
        @font-face {
          font-family: 'SamsungOne';
          font-style: normal;
          font-weight: 700;
          font-display: swap;
          src: url(/assets/fonts/SamsungOne-700_v2.0.woff) format('woff');
        }
        /* samsung One Korea */
        @font-face {
          font-family: 'SamsungOneKorean';
          font-style: normal;
          font-weight: 200;
          font-display: swap;
          src: url(/assets/fonts/SamsungOneKorean-200_v2.0_hinted_Light_20200508.woff2) format('woff2');
        }
        @font-face {
          font-family: 'SamsungOneKorean';
          font-style: normal;
          font-weight: 300;
          font-display: swap;
          src: url(/assets/fonts/SamsungOneKorean-300_v2.0_hinted_Light_20200508.woff2) format('woff2');
        }
        @font-face {
          font-family: 'SamsungOneKorean';
          font-style: normal;
          font-weight: 400;
          font-display: swap;
          src: url(/assets/fonts/SamsungOneKorean-400_v2.0_hinted_Light_20200508.woff2) format('woff2');
        }
        @font-face {
          font-family: 'SamsungOneKorean';
          font-style: normal;
          font-weight: 500;
          font-display: swap;
          src: url(/assets/fonts/SamsungOneKorean-500_v2.0_hinted_Light_20200508.woff2) format('woff2');
        }
        @font-face {
          font-family: 'SamsungOneKorean';
          font-style: normal;
          font-weight: 600;
          font-display: swap;
          src: url(/assets/fonts/SamsungOneKorean-600_v2.0_hinted_Light_20200508.woff2) format('woff2');
        }
        @font-face {
          font-family: 'SamsungOneKorean';
          font-style: normal;
          font-weight: 700;
          font-display: swap;
          src: url(/assets/fonts/SamsungOneKorean-700_v2.0_hinted_Light_20200508.woff2) format('woff2');
        }
        /* Samsung One Arabic */
        @font-face {
          font-family: 'SamsungOneArabic';
          font-style: normal;
          font-weight: 400;
          font-display: swap;
          src: url(/assets/fonts/SamsungOneArabic-400.woff2) format('woff');
        }
        @font-face {
          font-family: 'SamsungOneArabic';
          font-style: normal;
          font-weight: 700;
          font-display: swap;
          src: url(/assets/fonts/SamsungOneArabic-700.woff2) format('woff');
        }
        /* Samsung Sharp Sans */
        @font-face {
          font-family: 'SamsungSharpSans';
          font-style: normal;
          font-weight: bold;
          font-display: swap;
          src: url(/assets/fonts/SamsungSharpSansBd.woff2) format('woff');
        }
      `
      },
      MuiButton: {
        styleOverrides: {
          root: {
            minWidth: '216px',
            minHeight: '44px',
            fontFamily: ['SamsungOne', 'SamsungOneKorean'].join(','),
            fontSize: '17px',
            fontWeight: 700,
            lineHeight: '24px',
            textTransform: 'inherit',
            '&.Mui-disabled': {
              opacity: 0.4
            },
            '&:focus-visible': {
              outline: `2px solid ${isDark ? '#98CBFD' : '#0072DE'}`,
              outlineOffset: '2px'
            }
          },
          sizeSmall: {
            minWidth: '87px',
            minHeight: '36px',
            fontSize: '15px'
          },
          text: {
            fontSize: '16px'
          },
          contained: {
            borderRadius: '22px'
          },
          startIcon: {
            position: 'absolute',
            left: '6px',
            margin: '0px'
          },
          subButton: {
            color: theme.palette.mode === 'dark' ? '#FAFAFA' : '#252525',
            backgroundColor: theme.palette.mode === 'dark' ? '#636363' : '#E5E5E5',
            '&.Mui-disabled': {
              color: '#A9A9A9'
            }
          }
        }
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: '24px',
            border: '1px solid #fff',
            boxShadow: '0px 0.5px 3px rgba(0,0,0,0.35)',
            backgroundImage: 'none',
            padding: '40px 40px 0 40px'
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '24px',
            width: '100%',
            margin: '0',
            maxWidth: '500px',
            boxShadow: 'none',
            backgroundImage: 'none',
            padding: '36px 36px 20px 36px',
            backgroundColor: theme.palette.mode === 'dark' ? '#171717' : '#FAFAFA',
            [theme.breakpoints.only('xs')]: {
              padding: '24px 24px 20px 24px'
            }
          }
        }
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: '20px',
            fontWeight: '700',
            padding: '0 0 18px '
          }
        }
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            minHeight: '80px',
            padding: '0 0 24px',
            color: theme.palette.text.secondary
          }
        }
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '0',
            justifyContent: 'center',
            '& > :not(:first-of-type)': {
              marginLeft: '0'
            },
            '& button': {
              width: '100%',
              minWidth: '0',
              padding: '0',
              borderRadius: '22px',
              color: theme.palette.mode === 'dark' ? '#3E91FF' : '#387AFF'
            }
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: '40px',
            height: '26px',
            padding: '3px',
            '& .MuiSwitch-switchBase': {
              padding: '0px',
              width: '26px',
              height: '26px',
              transform: 'translateX(0px)',
              '&.Mui-checked': {
                color: theme.palette.background.paper,
                transform: 'translateX(14px)',
                '& .MuiSwitch-thumb': {
                  outline: `1.5px solid ${theme.palette.primary.main}`
                },
                '&.Mui-disabled .MuiSwitch-thumb': {
                  outline: `1.5px solid ${theme.palette.primary.main}60`
                },
                '& + .MuiSwitch-track': {
                  backgroundColor: theme.palette.primary.main,
                  opacity: 1,
                  transform: 'translateX(-3px)'
                }
              },
              '&.Mui-disabled .MuiSwitch-thumb': {
                outline: `1.5px solid ${theme.palette.gray[4]}`
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.4
              }
            },
            '& .MuiSwitch-thumb': {
              width: '23px',
              height: '23px',
              outline: `1.5px solid ${theme.palette.gray[2]}`,
              boxShadow: `0px 0px 2px ${theme.palette.text.primary}`
            },
            '& .MuiSwitch-track': {
              opacity: 1,
              backgroundColor: theme.palette.gray[2],
              borderRadius: '10px',
              width: '28px',
              transform: 'translateX(8px)'
            }
          }
        }
      },
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            listItemTitle: 'p',
            listItemDescription: 'p'
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            'label + &.MuiInput-root': {
              marginTop: '26px'
            }
          },
          input: {
            height: '26px',
            fontSize: '18px',
            fontWeight: 400,
            lineHeight: '26px',
            paddingTop: '0px',
            paddingBottom: '10px'
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '18px',
            fontWeight: 400,
            lineHeight: '24px',
            color: theme.palette.info.dark,
            transform: 'translate(0, 30px) scale(1)',
            '&.Mui-focused': {
              color: theme.palette.primary.main,
              transform: 'translate(0, 10px) scale(0.6666666667)'
            },
            '&.Mui-error': {
              color: theme.palette.error.main
            }
          },
          shrink: {
            color: theme.palette.gray[1],
            transform: 'translate(0, 10px) scale(0.6666666667)'
          }
        }
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginTop: '0px',
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: '16px'
          }
        }
      }
    }
  })
}
